import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Sidebar from '@/components/Sidebar';
import MobileNav from '@/components/MobileNav';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Plus, Calendar, MapPin, Trash2, AlertTriangle, Table2, ExternalLink } from 'lucide-react';
import { getEvents, deleteEvent } from '@/lib/api';
import { toast } from 'sonner';

const Events = ({ user }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, event: null });
  const [deleting, setDeleting] = useState(false);
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    loadEvents();
  }, []);

  const loadEvents = async () => {
    try {
      const response = await getEvents();
      setEvents(response.data);
    } catch (error) {
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (event) => {
    setDeleteDialog({ open: true, event });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteDialog.event) return;
    
    setDeleting(true);
    try {
      const response = await deleteEvent(deleteDialog.event.event_id);
      toast.success(`Event "${deleteDialog.event.name}" deleted successfully`);
      
      // Show what was deleted
      const deleted = response.data.deleted;
      if (deleted.budgets > 0 || deleted.expenses > 0 || deleted.transfers > 0) {
        toast.info(`Also deleted: ${deleted.budgets} budgets, ${deleted.expenses} expenses, ${deleted.transfers} transfers`);
      }
      
      setDeleteDialog({ open: false, event: null });
      loadEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete event');
    } finally {
      setDeleting(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-emerald-100 text-emerald-800';
      case 'completed': return 'bg-gray-100 text-gray-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      <Sidebar user={user} />
      
      <div className="flex-1 flex flex-col overflow-hidden md:ml-64">
        <header className="bg-white border-b border-border px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-foreground" style={{ fontFamily: 'Manrope, sans-serif' }}>Events</h2>
              <p className="text-sm text-muted-foreground">Manage your revival meetings</p>
            </div>
            <Link to="/events/new">
              <Button data-testid="create-event-button" className="shadow-sm">
                <Plus className="h-4 w-4 mr-2" />
                New Event
              </Button>
            </Link>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-6 pb-24 md:pb-6">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : events.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <Calendar className="h-16 w-16 text-muted-foreground mb-4" />
              <h3 className="text-xl font-semibold text-foreground mb-2">No events yet</h3>
              <p className="text-muted-foreground mb-6">Create your first revival meeting event</p>
              <Link to="/events/new">
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Event
                </Button>
              </Link>
            </div>
          ) : (
            <div className="max-w-6xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {events.map((event) => (
                  <Card key={event.event_id} className="card-hover" data-testid={`event-card-${event.event_id}`}>
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-3">
                        <h3 className="font-bold text-lg text-foreground">{event.name}</h3>
                        <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(event.status)}`}>
                          {event.status}
                        </span>
                      </div>
                      
                      {event.description && (
                        <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{event.description}</p>
                      )}
                      
                      <div className="space-y-2">
                        {event.location && (
                          <div className="flex items-center text-sm text-muted-foreground">
                            <MapPin className="h-4 w-4 mr-2" />
                            {event.location}
                          </div>
                        )}
                        {event.date && (
                          <div className="flex items-center text-sm text-muted-foreground">
                            <Calendar className="h-4 w-4 mr-2" />
                            {event.date}
                          </div>
                        )}
                      </div>

                      {/* Google Sheet Link */}
                      {event.spreadsheet_url && (
                        <div className="mt-3 pt-3 border-t border-border">
                          <a 
                            href={event.spreadsheet_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-center gap-2 text-sm text-emerald-600 hover:text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-md py-2 px-3 transition-colors"
                            data-testid={`sheet-link-${event.event_id}`}
                          >
                            <Table2 className="h-4 w-4" />
                            Open Google Sheet
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        </div>
                      )}

                      {/* Admin-only delete button */}
                      {isAdmin && (
                        <div className="mt-4 pt-4 border-t border-border">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteClick(event)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50 w-full"
                            data-testid={`delete-event-${event.event_id}`}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete Event
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </main>

        <MobileNav user={user} />
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, event: deleteDialog.event })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center text-red-600">
              <AlertTriangle className="h-5 w-5 mr-2" />
              Delete Event
            </DialogTitle>
            <DialogDescription className="pt-2">
              Are you sure you want to delete <strong>"{deleteDialog.event?.name}"</strong>?
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-800 font-medium mb-2">This will permanently delete:</p>
              <ul className="text-sm text-red-700 list-disc list-inside space-y-1">
                <li>The event itself</li>
                <li>All budgets for this event</li>
                <li>All expenses logged for this event</li>
                <li>All fund transfers to this event</li>
              </ul>
              <p className="text-sm text-red-800 font-semibold mt-3">This action cannot be undone!</p>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setDeleteDialog({ open: false, event: null })}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={deleting}
              data-testid="confirm-delete-event"
            >
              {deleting ? 'Deleting...' : 'Yes, Delete Everything'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Events;
